import React from "react";
import { Box, styled, useTheme, Button, IconButton, buttonClasses } from "@mui/material";
import DocumentIcon from "src/icons/DocumentIcon";
import { StyledTextField } from "@hyro-components/fields/text-field/HyroTextField.styles";
import { HyroButton } from "@hyro-components";
import {
  TEST_IDS,
  DEFAULT_BUTTON_LABEL
} from "./FileUploadField.consts";
import useFileUploadField from "../../hooks/FileUploadField.hook";
import CloseIcon from "src/icons/CloseIcon";
import { BUTTON_TEXT, getSupportedTypesText, DEFAULT_ACCEPTED_TYPES } from "../../FileUpload.consts";
import { truncateFileName } from "../../FileUpload.utils";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@hyro-components/hyro-button/HyroButton.consts";

const FileFieldContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  width: "100%",
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
}));

const FileUploadField = ({
  onFileSelect,
  isReadOnly = false,
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  error,
  value,
  helperText
}) => {
  const theme = useTheme();

  const {
    uploadedFiles,
    isUploading,
    open,
    getInputProps,
    getRootProps,
    removeFile,
    getFileError,
    handleFileDownload
  } = useFileUploadField({ acceptedFileTypes, onSubmit: onFileSelect, isReadOnly });

  const currentFile = uploadedFiles.length > 0 ? uploadedFiles[0] : null;
  const currentError = currentFile ? getFileError(currentFile) : null;
  const showError = (!!currentError || !!error) && !isReadOnly;
  const disabledClearButton = (!currentFile && !value) || isReadOnly || isUploading || showError;

  return (
    <Box data-testid={TEST_IDS.CONTAINER}>
      <FileFieldContainer>
        <StyledTextField
          variant="outlined"
          placeholder={getSupportedTypesText(acceptedFileTypes)}
          value={value?.file_name ? truncateFileName({ fileName: value.file_name, fileType: value.file_type }) : ""}
          readOnly={true}
          InputProps={{
            startAdornment: <DocumentIcon style={{ marginRight: "8px" }}
              color={isReadOnly || isUploading || (!value && !currentFile) ? theme.palette.text[600] : theme.palette.system[700]} />,
            endAdornment: !disabledClearButton && <IconButton size='small' onClick={() => removeFile(currentFile)} data-testid={TEST_IDS.REMOVE_FILE}>
              <CloseIcon color={theme.palette.system[700]} />
            </IconButton>,
            disabled: true
          }}
          error={showError}
          helperText={!isReadOnly ? currentError || helperText : ""}
          inputProps={{
            "data-testid": TEST_IDS.INPUT
          }}
        />

        <ButtonsContainer>
          <HyroButton
            variant={BUTTON_VARIANTS.OUTLINED}
            color={BUTTON_COLORS.PRIMARY}
            onClick={open}
            disabled={isReadOnly}
            data-testid={TEST_IDS.BUTTON}
            sx={{
              [`.${buttonClasses.endIcon}`]: {
                margin: 0
              }
            }}
            endIcon={<DocumentIcon style={{ display: isUploading ? "block" : "none" }} />}
            loadingPosition="end"
            loading={isUploading}
          >
            {DEFAULT_BUTTON_LABEL}
          </HyroButton>

          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={() => handleFileDownload(value)}
            disabled={disabledClearButton}
            data-testid={TEST_IDS.DOWNLOAD_BUTTON}
          >
            {BUTTON_TEXT.DOWNLOAD}
          </Button>
        </ButtonsContainer>
      </FileFieldContainer>

      {/* Input from useDropzone */}
      <div {...getRootProps()} style={{ display: "none" }}>
        <input {...getInputProps()} disabled={isReadOnly} />
      </div>
    </Box>
  );
};

export default FileUploadField;
